import { useMemoizedFn, useLatest } from 'ahooks';
import type { ISelectedSkillTagData } from '../SelectedSkillTag';
import type { ISkillData } from '@/pages/aigc/pages/Agent/SkillsCollection/types';
import { buildSkillInlineLabel } from '../../utils/inlineMentionLabel';
import { buildSelectedSkillTag } from '@/pages/aigc/pages/Agent/SkillsCollection/utils/skillTag';

export interface IUseSkillMentionKeyboardOptions {
  /** 当前是否处于 / 触发模式且下拉框已打开 */
  isSlashModeOpen: boolean;
  /** 过滤后的 skill 列表（与 SkillMention 中渲染一致） */
  filteredSkills: ISkillData[];
  /** 当前高亮索引 */
  highlightIndex: number;
  /** 设置高亮索引 */
  setHighlightIndex: React.Dispatch<React.SetStateAction<number>>;
  /** 已选中的 skills */
  selectedSkills: ISelectedSkillTagData[];
  /** 选中状态变更回调 */
  onSelectedChange: (skills: ISelectedSkillTagData[]) => void;
  /** 关闭下拉框 */
  closeDropdown: () => void;
  /** 清理 / 文本（选中后删除触发符并可插入引用串） */
  cleanupSlashText: (insertLabel?: string) => void;
  /** 是否英文环境 */
  isEN: boolean;
}

export interface IUseSkillMentionKeyboardResult {
  /**
   * 在 textarea keyDown 中调用，当 / 模式活跃时拦截 ArrowUp/Down/Enter/Escape。
   * 返回 true 表示事件已被消费，调用方应 return 跳过后续逻辑。
   */
  handleSlashModeKeyDown: (e: KeyboardEvent | React.KeyboardEvent) => boolean;
}

/**
 * 独立的键盘交互 hook —— 负责 / 模式下的上下键导航、回车选中、Escape 关闭。
 *
 * 设计原则：
 * - 只在 `isSlashModeOpen` 为 true 时拦截事件
 * - 与 SkillMention 组件解耦，通过 props 共享状态
 * - QAInput 直接调用，无需 ref 转发
 */
export const useSkillMentionKeyboard = ({
  isSlashModeOpen,
  filteredSkills,
  highlightIndex,
  setHighlightIndex,
  selectedSkills,
  onSelectedChange,
  closeDropdown,
  cleanupSlashText,
  isEN = false,
}: IUseSkillMentionKeyboardOptions): IUseSkillMentionKeyboardResult => {
  const filteredSkillsRef = useLatest(filteredSkills);
  const highlightIndexRef = useLatest(highlightIndex);
  const selectedSkillsRef = useLatest(selectedSkills);

  const handleSlashModeKeyDown = useMemoizedFn(
    (e: KeyboardEvent | React.KeyboardEvent): boolean => {
      if (!isSlashModeOpen) return false;

      const skills = filteredSkillsRef.current ?? [];
      const maxIdx = Math.max(skills.length - 1, 0);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          if (skills.length === 0) return true;
          setHighlightIndex((prev) => Math.min(prev + 1, maxIdx));
          return true;

        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          if (skills.length === 0) return true;
          setHighlightIndex((prev) => Math.max(prev - 1, 0));
          return true;

        case 'Enter': {
          // / 模式下 Enter 一律视为“面板内交互键”，必须消费，避免冒泡到输入框提交
          e.preventDefault();
          e.stopPropagation();

          const currentIdx = Math.min(highlightIndexRef.current, maxIdx);
          const skill = skills[currentIdx];
          if (!skill) return true;

          // 已选中的 skill 不做任何操作（不支持反选取消）
          const currentSelected = selectedSkillsRef.current ?? [];
          if (currentSelected.some((s) => s.skillId === skill.skill_id)) {
            return true;
          }

          const tagData: ISelectedSkillTagData = buildSelectedSkillTag(skill, isEN);
          onSelectedChange([...currentSelected, tagData]);
          cleanupSlashText(buildSkillInlineLabel(tagData, isEN));
          closeDropdown();
          return true;
        }

        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          closeDropdown();
          return true;

        default:
          return false;
      }
    },
  );

  return { handleSlashModeKeyDown };
};
